<script lang="ts">
	// Svelte
	import { createEventDispatcher, onMount } from 'svelte';
	// Services
	import { format } from 'svelte-i18n';
	// Design System
	import { Text, Token, Input, IconButton } from '@Shelly/shelly-design-system';
	// Stores
	import { user_settings } from 'src/stores';
	// Helpers
	import { capFirstChar } from 'src/helpers/utilities';
	import {
		hasCameraCloudStorage,
		hydrateFromUserSettings,
	} from 'src/helpers/camera_cloud_storage_helpers';
	import { getNumberInputConfig } from 'src/helpers/number_input_helpers';
	import { getDevicePropTranslation } from '../helpers/translation-helpers';
	import {
		CAMERA_MODE_MOTION_ZONES,
		type CameraMotionZone,
		buildCameraCondition,
		getEffectiveCameraDeviceProps,
		initializeCameraDeviceFromCondition,
		validateCameraCondition,
	} from 'src/components/Scenes/helpers/device_conditions/custom_cases/camera/camera-device-helpers';
	import {
		CAMERA_OBJECTS_LIMITS,
		CAMERA_OBJECT_TYPES,
		type CameraObjectsParam,
		buildCameraObjectsTransform,
		clampCameraObjectsParam,
		extractCameraObjectsParams,
		getCameraObjectTypeOptions,
		isCameraObjectsParamInRange,
	} from 'src/components/Scenes/helpers/device_conditions/custom_cases/camera/camera-objects-helpers';
	import { loadCameraObjectNames } from 'src/components/Camera/helpers/camera-objects';
	import { CAMERA_COND_NAMES } from '../helpers/camera-scene-helpers';
	import type { SceneConditionTransform } from 'src/types/ScenesConditionsTypes';

	export let sceneData: any = {};
	export let motionZones: CameraMotionZone[] = [];
	export let disabled = false;

	const dispatch = createEventDispatcher<{
		conditionChange: { condition: Record<string, unknown> };
		validationChange: { isValid: boolean };
		devicePropChange: { deviceProp: { title: string; value: string } };
	}>();

	const BOOLEAN_VALUE_OPTIONS = ['true', 'false'] as const;

	let selectedCameraMode = '';
	let selectedZone = '';
	let selectedValue = '';
	let objectType: string = CAMERA_OBJECT_TYPES[0];
	let objectCount: number = CAMERA_OBJECTS_LIMITS.nobjs.default;
	let objectConfidence: number = CAMERA_OBJECTS_LIMITS.minscore.default;
	let hasMounted = false;
	let availableObjectTypes: string[] = CAMERA_OBJECT_TYPES;

	$: objectTypeOptions = getCameraObjectTypeOptions(availableObjectTypes, objectType);

	$: cameraDeviceId = sceneData?.selectedDevice?.id ?? '';
	// Gated on `hasMounted` so it is recomputed once the slot store is hydrated.
	$: hasCloudStorage = hasMounted && hasCameraCloudStorage(cameraDeviceId);

	$: effectiveDevicePropsOptions = getEffectiveCameraDeviceProps($format, motionZones);

	$: currentZoneName = (() => {
		if (!selectedZone || !motionZones.length) {
			return '';
		}
		const zone = motionZones.find((z) => z.value === selectedZone);
		if (zone) {
			return zone.title;
		}
		return $format('label_zone_number', {
			default: 'Zone {n}',
			values: { n: selectedZone.replace('zone_', '') },
		});
	})();

	$: valueI18tr = (() => {
		if (selectedCameraMode === CAMERA_MODE_MOTION_ZONES) {
			return 'camera_zone_motion';
		}
		return selectedCameraMode;
	})();

	$: isObjectsMode = selectedCameraMode === CAMERA_COND_NAMES.OBJECTS;

	$: showValueSelector =
		Boolean(selectedCameraMode) &&
		!isObjectsMode &&
		(selectedCameraMode !== CAMERA_MODE_MOTION_ZONES || Boolean(selectedZone));

	$: transform = isObjectsMode
		? buildCameraObjectsTransform(objectType, objectCount, objectConfidence)
		: undefined;

	// `transform` is passed in so it is a dependency of this block, not read as stale state.
	$: {
		if (selectedCameraMode && selectedValue) {
			handleCameraCondition(transform);
		}
	}

	$: {
		const isValid = validateCameraCondition(
			selectedCameraMode,
			selectedZone,
			selectedValue,
			transform,
		);
		dispatch('validationChange', { isValid });
	}

	const initializeFromCondition = () => {
		const initialization = initializeCameraDeviceFromCondition(sceneData);
		if (!initialization) {
			return;
		}

		selectedCameraMode = initialization.selectedCameraMode;
		selectedZone = initialization.selectedZone;
		selectedValue = initialization.selectedValue;

		const objectsParams = extractCameraObjectsParams(initialization.transform);
		objectType = objectsParams.type;
		objectCount = objectsParams.nobjs;
		objectConfidence = objectsParams.minscore;
	};

	const handleCameraCondition = (currentTransform?: SceneConditionTransform) => {
		if (!selectedCameraMode || !selectedValue) {
			return;
		}

		const condition = buildCameraCondition(
			selectedCameraMode,
			selectedZone,
			selectedValue,
			currentTransform,
		);
		if (!condition) {
			return;
		}

		dispatch('conditionChange', {
			condition: {
				id: condition.conditionName,
				operator: condition.conditionOperator,
				value: condition.conditionValue,
				sensor_name: undefined,
				add_info_ind: condition.zoneAddInfoInd,
				input_index: undefined,
				transform: condition.transform,
			},
		});
	};

	const resetDeviceCondition = () => {
		if (!sceneData._device_condition) {
			return;
		}

		sceneData._device_condition.cond_name = '';
		sceneData._device_condition.operator = '';
		sceneData._device_condition.value = '0';
		sceneData._device_condition.add_info_ind = undefined;
		sceneData._device_condition.input_index = undefined;
		sceneData._device_condition.transform = undefined;
	};

	const onSelectDeviceProp = (deviceProp: { title: string; value: string }) => {
		selectedCameraMode = deviceProp.value;
		selectedZone = '';
		// The objects condition has no boolean state to pick — it always triggers on a match.
		selectedValue = deviceProp.value === CAMERA_COND_NAMES.OBJECTS ? 'true' : '';

		dispatch('devicePropChange', { deviceProp });

		resetDeviceCondition();

		sceneData = sceneData;
	};

	const onSelectZone = (zoneValue: string) => {
		selectedZone = zoneValue;
		selectedValue = '';

		resetDeviceCondition();

		sceneData = sceneData;
	};

	const onSelectValue = (value: string) => {
		selectedValue = value;

		if (sceneData._device_condition) {
			sceneData._device_condition.value = value;
		}

		sceneData = sceneData;
	};

	const onSelectObjectType = (type: string) => {
		objectType = type;
	};

	/**
	 * Keep whatever the user typed so the field stays editable, and let
	 * `objectsRangeError` block the wizard while the value is out of range.
	 */
	const onObjectsParamInput = (param: CameraObjectsParam, e: Event) => {
		const raw = (e.target as HTMLInputElement)?.value ?? '';
		const typed = raw === '' ? NaN : Number(raw);

		if (param === 'nobjs') {
			objectCount = typed;
		} else {
			objectConfidence = typed;
		}
	};

	const objectsParamText = (value: number) => (Number.isFinite(value) ? String(value) : '');

	const adjustObjectsParam = (param: CameraObjectsParam, delta: number) => {
		const step = CAMERA_OBJECTS_LIMITS[param].step * delta;

		if (param === 'nobjs') {
			objectCount = clampCameraObjectsParam(param, objectCount + step);
		} else {
			objectConfidence = clampCameraObjectsParam(param, objectConfidence + step);
		}
	};

	const objectsRangeError = (param: CameraObjectsParam, value: number) => {
		if (isCameraObjectsParamInRange(param, value)) {
			return undefined;
		}

		const { min, max } = CAMERA_OBJECTS_LIMITS[param];
		const unit = param === 'minscore' ? '%' : '';
		return $format('error_out_of_range', {
			values: { value, min, max, unit },
			default: `Value ${value}${unit} is out of range. Please provide a value within the limits. (${min}${unit} — ${max}${unit})`,
		});
	};

	onMount(async () => {
		// Nothing else in the scene wizard hydrates the camera slot store.
		hydrateFromUserSettings($user_settings);

		if (sceneData.when_info?.mode === 'edit') {
			initializeFromCondition();
		}

		hasMounted = true;
		availableObjectTypes = await loadCameraObjectNames();
	});
</script>

<div class="d-flex flex-wrap gap-s">
	{#each effectiveDevicePropsOptions as deviceProp}
		<Token
			id="token-camera-prop-{deviceProp.value}"
			title={capFirstChar(deviceProp.title)}
			class="text-capitalize"
			on:click={() => onSelectDeviceProp(deviceProp)}
			selected={selectedCameraMode === deviceProp.value}
			disabled={deviceProp.disabled || disabled}
		/>
	{/each}
</div>

{#if selectedCameraMode}
	<div class="mt-m">
		{#if selectedCameraMode === CAMERA_MODE_MOTION_ZONES && motionZones.length > 0}
			<Text class="mb-s" styleType="h2">
				{$format('select_zone', { default: 'Select Zone' })}
			</Text>
			<div class="d-flex flex-wrap mb-l gap-s">
				{#each motionZones as zone}
					<Token
						id="token-camera-zone-{zone.id}"
						title={capFirstChar(zone.title)}
						size="m"
						selected={selectedZone === zone.value}
						on:click={() => onSelectZone(zone.value)}
						{disabled}
					/>
				{/each}
			</div>
		{/if}

		{#if showValueSelector}
			<Text class="mb-s" styleType="h2">
				{#if selectedCameraMode === CAMERA_MODE_MOTION_ZONES}
					{$format('select_motion_state_for_zone', {
						values: { zoneName: currentZoneName },
						default: `Motion state for ${currentZoneName}`,
					})}
				{:else}
					{getDevicePropTranslation(valueI18tr, '', undefined, '')?.title}
				{/if}
			</Text>
			<div class="d-flex flex-wrap mb-l gap-s">
				{#each BOOLEAN_VALUE_OPTIONS as boolValue}
					{@const currentTranslation = getDevicePropTranslation(
						valueI18tr,
						'',
						undefined,
						boolValue,
					)}
					<Token
						id="token-camera-value-{boolValue}"
						title={currentTranslation?.condition}
						size="m"
						icon={currentTranslation?.icons[boolValue]}
						selected={selectedValue === boolValue}
						on:click={() => onSelectValue(boolValue)}
						{disabled}
					/>
				{/each}
			</div>
		{/if}

		{#if isObjectsMode}
			<Text class="mb-s" styleType="h2">
				{$format('camera_objects_select_type', { default: 'Select object type' })}
			</Text>
			<div class="d-flex flex-wrap mb-l gap-s">
				{#each objectTypeOptions as option}
					<Token
						id="token-camera-object-type-{option.value}"
						title={capFirstChar(option.title)}
						size="m"
						icon={option.icon}
						selected={objectType === option.value}
						on:click={() => onSelectObjectType(option.value)}
						{disabled}
					/>
				{/each}
			</div>

			<Text class="mb-s" styleType="h2">
				{$format('camera_objects_count', { default: 'Minimum number of objects' })}
			</Text>
			<div class="d-flex mt-s mb-l">
				<IconButton
					aria-label={$format('button_decrease_value', { default: 'Decrease value' })}
					icon="fas fa-minus"
					on:click={() => adjustObjectsParam('nobjs', -1)}
					disabled={disabled || objectCount <= CAMERA_OBJECTS_LIMITS.nobjs.min}
				/>
				<Input
					aria-label={$format('camera_objects_count', {
						default: 'Minimum number of objects',
					})}
					{...getNumberInputConfig(true)}
					class="width-md-s width-sm-full ml-s mr-s"
					size="m"
					value={objectsParamText(objectCount)}
					error={objectsRangeError('nobjs', objectCount)}
					on:input={(e) => onObjectsParamInput('nobjs', e)}
					min={String(CAMERA_OBJECTS_LIMITS.nobjs.min)}
					max={String(CAMERA_OBJECTS_LIMITS.nobjs.max)}
					id="input-camera-objects-count"
					{disabled}
				/>
				<IconButton
					aria-label={$format('button_increase_value', { default: 'Increase value' })}
					icon="fas fa-plus"
					on:click={() => adjustObjectsParam('nobjs', 1)}
					disabled={disabled || objectCount >= CAMERA_OBJECTS_LIMITS.nobjs.max}
				/>
			</div>

			<Text class="mb-s" styleType="h2">
				{$format('camera_objects_confidence', { default: 'Minimum confidence (%)' })}
			</Text>
			<div class="d-flex mt-s mb-l">
				<IconButton
					aria-label={$format('button_decrease_value', { default: 'Decrease value' })}
					icon="fas fa-minus"
					on:click={() => adjustObjectsParam('minscore', -1)}
					disabled={disabled || objectConfidence <= CAMERA_OBJECTS_LIMITS.minscore.min}
				/>
				<Input
					aria-label={$format('camera_objects_confidence', {
						default: 'Minimum confidence (%)',
					})}
					{...getNumberInputConfig(true)}
					class="width-md-s width-sm-full ml-s mr-s"
					size="m"
					value={objectsParamText(objectConfidence)}
					error={objectsRangeError('minscore', objectConfidence)}
					on:input={(e) => onObjectsParamInput('minscore', e)}
					min={String(CAMERA_OBJECTS_LIMITS.minscore.min)}
					max={String(CAMERA_OBJECTS_LIMITS.minscore.max)}
					id="input-camera-objects-confidence"
					{disabled}
				/>
				<IconButton
					aria-label={$format('button_increase_value', { default: 'Increase value' })}
					icon="fas fa-plus"
					on:click={() => adjustObjectsParam('minscore', 1)}
					disabled={disabled || objectConfidence >= CAMERA_OBJECTS_LIMITS.minscore.max}
				/>
			</div>
		{/if}
	</div>
{/if}
